/**
 * Logica PURA della scadenza dell'attività veloce del composer inline (nessun import:
 * niente React, niente store, niente Supabase — testabile a sé).
 *
 * Il composer offre le stesse scorciatoie della Dashboard (`InlineActivityInput`):
 * Oggi / Domani / Tra 3gg, oppure una data libera ("Altra"), sempre combinate con un'ora
 * (default 09:00). Qui si risolve la coppia scorciatoia+ora in un `Date` locale.
 *
 * Differenza VOLUTA rispetto alla Dashboard: la data libera viene ricostruita dai suoi
 * componenti (`new Date(y, m - 1, d, ...)`) invece che con `new Date('YYYY-MM-DD')`, che
 * il motore JS interpreta come mezzanotte UTC — a est di Greenwich l'ora impostata poteva
 * finire sul giorno sbagliato.
 */

export interface ComposerDueDateInput {
  /** Giorni da oggi della scorciatoia selezionata; `null` quando vale `customDate`. */
  offsetDays: number | null;
  /** Data libera in formato `YYYY-MM-DD` (valore di un `<input type="date">`), o stringa vuota. */
  customDate: string;
  /** Ora in formato `HH:mm`. Valori non validi ricadono sul default 09:00. */
  time: string;
}

export const COMPOSER_DEFAULT_TIME = '09:00';

/** Durata di default di un'attività veloce: 15 minuti, come il form del drawer. */
export const COMPOSER_DEFAULT_DURATION_MIN = 15;

const TIME_RE = /^(\d{1,2}):(\d{2})$/;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `HH:mm` → `{hours, minutes}`; qualunque input non valido ricade su 09:00. */
export function parseComposerTime(time: string): { hours: number; minutes: number } {
  const match = TIME_RE.exec((time || '').trim());
  if (!match) return { hours: 9, minutes: 0 };
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return { hours: 9, minutes: 0 };
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return { hours: 9, minutes: 0 };
  return { hours, minutes };
}

/** Scorciatoia + ora → istante di scadenza (fuso locale). */
export function resolveComposerDueDate(input: ComposerDueDateInput, now: Date = new Date()): Date {
  const { hours, minutes } = parseComposerTime(input.time);
  const custom = DATE_RE.exec((input.customDate || '').trim());
  if (custom) {
    return new Date(Number(custom[1]), Number(custom[2]) - 1, Number(custom[3]), hours, minutes, 0, 0);
  }
  const offset = Number.isFinite(input.offsetDays as number) ? (input.offsetDays as number) : 0;
  const date = new Date(now.getTime());
  date.setDate(date.getDate() + offset);
  date.setHours(hours, minutes, 0, 0);
  return date;
}

/** Fine di default dell'attività veloce (inizio + 15 minuti), come il form del drawer. */
export function resolveComposerEndDate(start: Date): Date {
  return new Date(start.getTime() + COMPOSER_DEFAULT_DURATION_MIN * 60 * 1000);
}

/**
 * `Date` → `YYYY-MM-DD` LOCALE, formato atteso da `initialActivityData.due_date` della
 * shell del drawer (che la ricompone con `new Date(\`${due_date}T09:00:00\`)`).
 * `toISOString().split('T')[0]` qui sarebbe sbagliato: converte in UTC.
 */
export function toComposerDateInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * `Date` → `HH:mm` LOCALE, formato di `initialActivityData.due_time`. Senza questa metà
 * l'escalation dal composer al drawer perderebbe l'ora scelta, che la shell ricadrebbe a
 * riproporre alle 09:00.
 */
export function toComposerTimeInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
